import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import { Languages, ArrowRight, Globe } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";

const TRANSLATION_LANGUAGES = [
  { code: "pt", name: "Português", nativeName: "Português" },
  { code: "en", name: "English", nativeName: "English" },
  { code: "es", name: "Español", nativeName: "Español" },
  { code: "fr", name: "Français", nativeName: "Français" },
  { code: "de", name: "Deutsch", nativeName: "Deutsch" },
  { code: "ru", name: "Русский", nativeName: "Russkiy" },
  { code: "ja", name: "日本語", nativeName: "Nihongo" },
  { code: "ko", name: "한국어", nativeName: "Hangugeo" },
  { code: "ar", name: "العربية", nativeName: "Al-Arabiyyah" },
  { code: "fil", name: "Filipino", nativeName: "Filipino" },
  { code: "zh", name: "中文", nativeName: "Zhongwen" },
  { code: "hi", name: "हिन्दी", nativeName: "Hindi" },
  { code: "it", name: "Italiano", nativeName: "Italiano" },
  { code: "nl", name: "Nederlands", nativeName: "Nederlands" },
  { code: "pl", name: "Polski", nativeName: "Polski" },
  { code: "tr", name: "Türkçe", nativeName: "Türkçe" },
  { code: "vi", name: "Tiếng Việt", nativeName: "Tieng Viet" },
  { code: "th", name: "ไทย", nativeName: "Thai" },
  { code: "sv", name: "Svenska", nativeName: "Svenska" },
  { code: "uk", name: "Українська", nativeName: "Ukrainska" },
];

export default function LanguagePairSelection() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  
  // Source language is pre-filled with the UI language selected on the Home page
  const [sourceLanguage, setSourceLanguage] = useState(language);
  const [targetLanguage, setTargetLanguage] = useState("");

  const handleStartTranslation = () => {
    if (sourceLanguage && targetLanguage) {
      // Store the translation pair in localStorage
      localStorage.setItem("translation_source_lang", sourceLanguage);
      localStorage.setItem("translation_target_lang", targetLanguage);
      navigate("/dashboard");
    }
  };

  // Get source language display name
  const getLanguageName = (code) => {
    const lang = TRANSLATION_LANGUAGES.find(l => l.code === code);
    return lang ? lang.name : code;
  };

  // Filter out source language from target options
  const targetOptions = TRANSLATION_LANGUAGES.filter(l => l.code !== sourceLanguage);

  return (
    <div className="min-h-screen bg-zinc-950 flex flex-col items-center justify-center p-4 sm:p-6">
      {/* Logo and Title */}
      <div className="text-center mb-6 sm:mb-10">
        <div className="flex items-center justify-center gap-3 mb-4 sm:mb-6">
          <Languages className="w-10 h-10 sm:w-12 sm:h-12 text-white" />
        </div>
        
        <h1 
          className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3 sm:mb-4 font-['Outfit'] max-w-2xl px-4"
          data-testid="language-pair-title"
        >
          {t("selectTranslationLanguages")}
        </h1>
      </div>

      {/* Language Selection Cards */}
      <div className="flex flex-col md:flex-row items-center gap-4 sm:gap-6 max-w-4xl w-full px-2">
        {/* Source Language Card */}
        <Card className="flex-1 w-full bg-zinc-900/50 border-zinc-800" data-testid="source-language-card">
          <CardContent className="p-4 sm:p-6">
            <Label className="text-zinc-400 text-xs sm:text-sm mb-2 sm:mb-3 block">
              {t("sourceLanguage")}
            </Label>
            <Select value={sourceLanguage} onValueChange={setSourceLanguage}>
              <SelectTrigger 
                className="bg-zinc-800 border-zinc-700 text-white h-12 sm:h-14 text-base sm:text-lg"
                data-testid="source-language-select"
              >
                <div className="flex items-center gap-2 sm:gap-3">
                  <Globe className="w-4 h-4 sm:w-5 sm:h-5 text-zinc-400 flex-shrink-0" />
                  <SelectValue placeholder={t("selectSourceLanguage")} />
                </div>
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700 max-h-[300px]">
                {TRANSLATION_LANGUAGES.map((lang) => (
                  <SelectItem 
                    key={lang.code} 
                    value={lang.code}
                    className="text-white hover:bg-zinc-800"
                  >
                    <span className="flex items-center gap-2">
                      <span>{lang.name}</span>
                      {lang.name !== lang.nativeName && (
                        <span className="text-zinc-500 text-xs sm:text-sm">({lang.nativeName})</span>
                      )}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Arrow */}
        <div className="flex items-center justify-center">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-zinc-800 flex items-center justify-center">
            <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6 text-white md:rotate-0 rotate-90" />
          </div>
        </div>

        {/* Target Language Card */}
        <Card className="flex-1 w-full bg-zinc-900/50 border-zinc-800" data-testid="target-language-card">
          <CardContent className="p-4 sm:p-6">
            <Label className="text-zinc-400 text-xs sm:text-sm mb-2 sm:mb-3 block">
              {t("targetLanguage")}
            </Label>
            <Select value={targetLanguage} onValueChange={setTargetLanguage}>
              <SelectTrigger 
                className={`bg-zinc-800 border-zinc-700 text-white h-12 sm:h-14 text-base sm:text-lg ${!targetLanguage ? 'border-dashed border-2' : ''}`}
                data-testid="target-language-select"
              >
                <div className="flex items-center gap-2 sm:gap-3">
                  <Globe className="w-4 h-4 sm:w-5 sm:h-5 text-zinc-400 flex-shrink-0" />
                  <SelectValue placeholder={t("selectTargetLanguage")} />
                </div>
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700 max-h-[300px]">
                {targetOptions.map((lang) => (
                  <SelectItem 
                    key={lang.code} 
                    value={lang.code}
                    className="text-white hover:bg-zinc-800"
                  >
                    <span className="flex items-center gap-2">
                      <span>{lang.name}</span>
                      {lang.name !== lang.nativeName && (
                        <span className="text-zinc-500 text-xs sm:text-sm">({lang.nativeName})</span>
                      )}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      </div>

      {/* Hint text */}
      <p className="mt-4 sm:mt-6 text-zinc-500 text-xs sm:text-sm text-center max-w-md px-4">
        {t("languagePairHint")}
      </p>

      {/* Start Button */}
      <Button
        onClick={handleStartTranslation}
        disabled={!sourceLanguage || !targetLanguage}
        className="mt-6 sm:mt-8 btn-primary px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg"
        data-testid="start-translation-btn"
      >
        {t("startTranslation")}
        <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
      </Button>

      {/* Selected pair preview */}
      {sourceLanguage && targetLanguage && (
        <div className="mt-4 sm:mt-6 px-4 sm:px-6 py-2 sm:py-3 bg-zinc-900/50 border border-zinc-800 rounded-full">
          <span className="text-white font-medium text-sm sm:text-base">
            {getLanguageName(sourceLanguage)} → {getLanguageName(targetLanguage)}
          </span>
        </div>
      )}

      {/* Copyright */}
      <p className="mt-8 sm:mt-12 text-zinc-700 text-[10px] sm:text-xs text-center px-4">
        {t("footerCopyright")}
      </p>
    </div>
  );
}
